/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';

export interface UseFilterChipSelectProps {
  selectedIds: string[];
  assignedIds: string[];
  onToggleSelect?: (id: string) => void;
  onSelectMultiple?: (ids: string[]) => void;
}

interface UseFilterChipSelect {
  handleChipClick: (id: string, event: React.MouseEvent) => void;
}

export function useFilterChipSelect({
  selectedIds,
  assignedIds,
  onToggleSelect,
  onSelectMultiple,
}: UseFilterChipSelectProps): UseFilterChipSelect {
  const lastSelectedByClick = React.useRef<string | null>(null);

  // reset lastSelectedByClick when it disappears from ids list
  React.useEffect(() => {
    if (
      lastSelectedByClick.current &&
      (!assignedIds.includes(lastSelectedByClick.current) ||
        !selectedIds.includes(lastSelectedByClick.current))
    ) {
      lastSelectedByClick.current = null;
    }
  }, [assignedIds, selectedIds]);

  return {
    handleChipClick(id: string, event: React.MouseEvent): void {
      if (event.shiftKey && lastSelectedByClick.current) {
        const assignedAreasIdsArray = Array.from(assignedIds);
        const lastClickedIndex = assignedAreasIdsArray.indexOf(
          lastSelectedByClick.current,
        );
        const clickedIndex = assignedAreasIdsArray.indexOf(id);
        const firstIndex = Math.min(clickedIndex, lastClickedIndex);
        const secondIndex = Math.max(clickedIndex, lastClickedIndex);

        onSelectMultiple?.(
          assignedAreasIdsArray.slice(firstIndex, secondIndex + 1),
        );
      } else {
        const idIsSelected = selectedIds.includes(id);
        if (!idIsSelected) {
          lastSelectedByClick.current = id;
        } else {
          lastSelectedByClick.current = null;
        }
        onToggleSelect?.(id);
      }
    },
  };
}
